import React, { useEffect, useState } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import api from '../../services/api';
import GridMui from '../../components/gridMui';
import Loading from '../../components/loading';
import {
  Container,
  Flex,
  Card,
  CardHeader,
  CardContent,
  Badge,
} from '../../components/ui';
import { useTheme } from '../../context/ThemeContext';

const LogsPage: React.FC = () => {
  const [logs, setLogs] = useState<any>([]);
  const [column, setColumn] = useState<any[]>([]);
  const { isDarkMode } = useTheme();

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
    },
    components: {
      // @ts-expect-error - MuiDataGrid is from @mui/x-data-grid
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1C1C1E' : '#FFFFFF',
            color: isDarkMode ? '#FFFFFF' : '#263238',
            border: `1px solid ${isDarkMode ? '#48484A' : '#E5E5EA'}`,
            '& .MuiDataGrid-cell': {
              color: isDarkMode ? '#FFFFFF' : '#263238',
              borderColor: isDarkMode ? '#48484A' : '#E5E5EA',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: isDarkMode ? '#2C2C2E' : '#F5F5F5',
              color: isDarkMode ? '#FFFFFF' : '#263238',
              borderColor: isDarkMode ? '#48484A' : '#E5E5EA',
            },
            '& .MuiDataGrid-columnHeader': {
              color: isDarkMode ? '#FFFFFF' : '#263238',
            },
            '& .MuiDataGrid-row': {
              backgroundColor: isDarkMode ? '#1C1C1E' : '#FFFFFF',
              '&:nth-of-type(even)': {
                backgroundColor: isDarkMode ? '#2C2C2E' : '#FAFAFA',
              },
              '&:hover': {
                backgroundColor: isDarkMode ? '#3A3A3C' : '#F0F0F0',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: isDarkMode ? '#2C2C2E' : '#F5F5F5',
              color: isDarkMode ? '#FFFFFF' : '#263238',
              borderColor: isDarkMode ? '#48484A' : '#E5E5EA',
            },
          },
        },
      },
    },
  });

  const renderCell = (params: GridRenderCellParams): React.ReactNode => {
    const severityMap: Record<string, 'success' | 'warning' | 'error'> = {
      success: 'success',
      warning: 'warning',
      error: 'error',
    };

    const variant = severityMap[params.row.type] || 'info';

    return (
      <Flex align="center" style={{ height: '100%', padding: '4px 0' }}>
        <Badge variant={variant} size="small">
          {params.value}
        </Badge>
      </Flex>
    );
  };

  useEffect(() => {
    api
      .get('/logging')
      .then((response: any) => {
        setLogs(response.data);
      })
      .catch((error: any) => {
        console.error(error.response.data.message);
      });
  }, []);

  useEffect(() => {
    if (logs.length > 0) {
      const columns: GridColDef<(typeof logs)[number]>[] = [
        {
          field: 'user',
          headerName: 'User',
          flex: 1,
          editable: false,
        },
        {
          field: 'createdAt',
          headerName: 'Created At',
          flex: 1,
          editable: false,
        },
        {
          field: 'action',
          headerName: 'Action',
          flex: 1,
          editable: false,
        },
        {
          field: 'type',
          headerName: 'Severity',
          flex: 1,
          editable: false,
          renderCell,
        },
        {
          field: 'message',
          headerName: 'Message',
          flex: 2,
          editable: false,
        },
      ];

      setColumn(columns);
    }
  }, [logs.length]);

  return (
    <Container maxWidth="full" padding>
      <Card variant="elevated" padding="large" fullHeight>
        <CardHeader>
          <h1 style={{ margin: 0 }}>System Logs</h1>
        </CardHeader>
        <CardContent>
          <ThemeProvider theme={muiTheme}>
            {logs.length > 0 ? (
              <GridMui columns={column} rows={logs} />
            ) : (
              <Flex
                justify="center"
                align="center"
                style={{ minHeight: '400px' }}
              >
                <Loading />
              </Flex>
            )}
          </ThemeProvider>
        </CardContent>
      </Card>
    </Container>
  );
};

export default LogsPage;
